import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { PageHeaderStore } from '../../../../shared/stores/page-header.store';
import { SessionStore } from '../../../../shared/stores/session.store';
import { ROUTE_LOGIN } from '../../../../shared/constants/routes';
import { AuthService } from '../../../../shared/services/auth.service';
import { DialogService } from '../../../../shared/services/dialog.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [MatMenuModule, MatIconModule, MatButtonModule, RouterLink],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header {
  // Injected dependencies
  private readonly router = inject(Router);
  private readonly pageHeaderStore = inject(PageHeaderStore);
  private readonly sessionStore = inject(SessionStore);
  private readonly authService = inject(AuthService);
  private readonly dialogService = inject(DialogService);

  /** Page title from the active route, shown beside profile actions */
  readonly pageTitle = this.pageHeaderStore.title;

  /** Optional breadcrumb back link from the active route */
  readonly breadcrumbBack = this.pageHeaderStore.breadcrumbBack;

  /** Current session; undefined = loading, null = not authenticated */
  readonly session = this.sessionStore.currentSession;

  /** Display name from session, or fallback */
  readonly displayName = computed(() => {
    const s = this.session();
    if (!s?.name?.trim()) return 'Anonymous';
    return s.name.trim();
  });

  /** First letter for avatar from session name */
  readonly avatarLetter = computed(() => {
    const s = this.session();
    if (!s?.name?.trim()) return 'A';
    return s.name.trim().charAt(0).toUpperCase();
  });

  /** Email if present on session */
  readonly email = computed(() => {
    const s = this.session();
    return s?.email || '';
  });

  /**
   * Perform logout and navigate back to login
   */
  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate([ROUTE_LOGIN]);
      },
    });
  }
}
